<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-0">
        <v-col cols="3">
          <date-range :from.sync="from" :to.sync="to" v-model="dateDisplay" @change="refresh()" hide-details option="Last 30 days"/>
        </v-col>
        <v-col cols="3">
          <v-autocomplete label="Wine" dense outlined hide-details :items="wines" item-text="name" item-value="id" v-model="wineId"/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Wine fermenting activity', report())"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 184px)">
      <span v-html="report()"/>
    </v-card>

  </div>
</template>

<script>
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'

export default {
  name: 'ReportsWineFermentingActivity',
  mixins: [printHtml],
  data() {
    return {
      from: null,
      to: null,
      dateDisplay: '',
      wines: [{name:'All wine',id:null},{divider:true}],
      wineId: null,
      wineFermentations: [],
      loading: true,
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    wineName() {
      return this.wines.find(wine => wine.id === this.wineId)?.name ?? '';
    },
    wineFermentationsList() {
      if (this.wineId === null)
        return this.wineFermentations;
      return this.wineFermentations.filter(fermentation =>
        (this.wineId === '' && fermentation.wineId === null) || (this.wineId !== '' && this.wineId === fermentation.wineId)
      );
    }
  },
  async created() {
    this.$emit('name', 'Wine fermenting activity')
    if (this.$route.query.from && this.$route.query.to) {
      this.from = this.$route.query.from;
      this.to = this.$route.query.to;
    }

    this.$lazyListLoad(this.wines, 'wines2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]);
    this.loading = false;
  },
  methods: {
    async refresh() {
      this.loading = true;
      const results = await this.$apiCall(`query ReportsWineFermentingActivity($from:Date!,$to:Date!) {
                                                wineFermentationActivity(from:$from,to:$to) {
                                                  id,date,wineId,wineName,number
                                                  inputs{name,volume,units},
                                                  outputs{name,volume}
                                                }
                                              }`,
                                          {from:this.from,to:this.to});
      if (!results.error)
        this.wineFermentations = results.data.wineFermentationActivity;
      this.loading = false;
    },

    report() {
    const lines = [
                    `<style>
                      @page {size:landscape}
                      .reports-activity {font-family: Roboto, sans-serif; font-size:14px;width:100%;border-spacing:0}
                      .reports-activity th {height:36px;padding:0 16px}
                      .reports-activity td {height:36px;padding:0 16px}
                      .reports-activity .title {font-size:18px;text-transform:uppercase}
                      .reports-activity .sup-title {font-weight:normal;font-size:22px}
                      .reports-activity .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                      .reports-activity .header th {border-bottom:1px solid #000;margin-bottom:20px}
                      .reports-activity .text-left {text-align:left}
                      .reports-activity .text-right {text-align:right}
                    </style>
                    <table class="reports-activity">
                     <thead>
                        <tr>
                          <th colspan="5" class="sup-title">${this.company.name}</th>
                        </tr>
                        <tr>
                          <th colspan="5" class="title">Wine Fermenting activity</th>
                        </tr>
                        <tr>
                           <th colspan="5" class="sub-title">${this.dateDisplay}</th>
                        </tr>
                        <tr class="header">
                            <th width="15%" class="text-left">Date</th>
                            <th width="20%" class="text-left">Number</th>
                            <th width="20%" class="text-left">Wine produced</th>
                            <th width="10%" class="text-left">In/Out</th>
                            <th width="25%" class="text-left">Item/Tank</th>
                            <th width="20%" class="text-left text-right">Quantity</th>
                          </tr>
                      </thead>
                      <tbody>`
    ];
    for (const wineFermentation of this.wineFermentationsList) {
      let i = 0;
      for (const input of wineFermentation.inputs) {
        lines.push(`<tr>`);
        if (i === 0) {
          lines.push(`<td>${this.$formatDate(wineFermentation.date)}</td>`);
          lines.push(`<td>${wineFermentation.number}</td>`);
          lines.push(`<td>${wineFermentation.wineName}</td>`);
        }
        else {
          lines.push(`<td/>`)
          lines.push(`<td/>`)
          lines.push(`<td/>`)
        }
        lines.push(`<td>IN</td>`);
        lines.push(`<td>${input.name}</td>`);
        lines.push(`<td class="text-right">${input.volume} ${input.units}</td>`);
        lines.push(`</tr>`);
        i++
      }
      for (const output of wineFermentation.outputs) {
        lines.push(`<tr>`);
        lines.push(`<td/>`)
        lines.push(`<td/>`)
        lines.push(`<td/>`)
        lines.push(`<td>OUT</td>`);
        lines.push(`<td>${output.name}</td>`);
        lines.push(`<td class="text-right">${output.volume} litres</td>`);
        lines.push(`</tr>`);
        i++
      }
    }
    lines.push(`</tbody>`);
    lines.push(`</table>`);
    return lines.join('');
    },

    exportCSV() {
      const lines = [ `"${this.company.name}",,,,`,
                      `"Wine fermentation activity",,,,`,
                      `"${this.dateDisplay}",,,,`,
                      `"Date","Number","Wine","In/Out","Item/Fermenter","Volume"`
                    ];
      for (const wineFermentation of this.wineFermentationsList) {
        let i = 0;
        for (const input of wineFermentation.inputs) {
          let line = i === 0 ? `"${wineFermentation.date}","${wineFermentation.number}","${wineFermentation.wineName}",` : `,,`;
          line += `"IN","${input.name}","${input.volume} ${input.units}"`;
          lines.push(line);
          i++
        }
        for (const output of wineFermentation.outputs) {
          lines.push(`,,"OUT","${output.name}","${output.volume}"`);
          i++;
        }
      }

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Wine fermentation activity.csv');
    }
  }
}
</script>